import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { EnvironmentToken } from '@jump-tech-frontend/app-config';
import {
  ClassificationResult,
  ProjectAnalysesResponse,
  ProjectAnalysis,
  RelayEnvironment
} from '@jump-tech-frontend/domain';
import { map, Observable, of, shareReplay, switchMap, tap, timeout } from 'rxjs';

export interface StoredAsset {
  projectId: string;
  assetKey: string;
}

const STORE_PATH_PATTERN = /\/store\/[^/]+\/[^/]+\/([^/?#]+)/;

export function parseStoredAsset(imageUrl: string): StoredAsset | null {
  const match = STORE_PATH_PATTERN.exec(imageUrl);
  if (!match?.[1]) {
    return null;
  }

  let assetKey: string;
  try {
    assetKey = decodeURIComponent(match[1]);
  } catch {
    return null;
  }

  const projectId = assetKey.split('/')[0];
  if (!projectId || projectId === assetKey) {
    return null;
  }

  return { projectId, assetKey };
}

@Injectable({ providedIn: 'root' })
export class ImageAnalysisRecordService {
  private http = inject(HttpClient);
  private activatedRoute = inject(ActivatedRoute, { optional: true });
  private environment = inject<RelayEnvironment | null>(EnvironmentToken, { optional: true });

  private readonly REQUEST_TIMEOUT_MS = 10000;
  private readonly byProject = new Map<string, Observable<Map<string, ProjectAnalysis>>>();
  private readonly byAnalysisId = new Map<string, ClassificationResult>();
  private suid = '';

  constructor() {
    this.subscribeToQueryParams();
  }

  remember(result: ClassificationResult): void {
    if (result.analysisId) {
      this.byAnalysisId.set(result.analysisId, result);
    }
  }

  rememberedAnalysis(analysisId: string | undefined): ClassificationResult | null {
    return (analysisId && this.byAnalysisId.get(analysisId)) || null;
  }

  getAnalysisForAsset(asset: StoredAsset): Observable<ClassificationResult | null> {
    if (!this.apiUrl) {
      return of(null);
    }

    const snapshot = this.getProjectAnalyses(asset.projectId);

    return snapshot.pipe(
      switchMap(analyses => {
        const analysis = analyses.get(asset.assetKey);
        if (analysis) {
          return of(analysis);
        }

        return this.rereadProjectAnalyses(asset.projectId, snapshot).pipe(
          map(reread => reread.get(asset.assetKey) ?? null)
        );
      })
    );
  }

  private get apiUrl(): string | undefined {
    return this.environment?.apiImageAnalysisUrl;
  }

  private getProjectAnalyses(projectId: string): Observable<Map<string, ProjectAnalysis>> {
    const inFlight = this.byProject.get(projectId);
    if (inFlight) {
      return inFlight;
    }

    const request = this.http
      .get<ProjectAnalysesResponse>(this.apiUrl as string, { headers: this.buildHeaders(), params: { projectId } })
      .pipe(
        timeout(this.REQUEST_TIMEOUT_MS),
        map(response => indexByAssetKey(response?.analyses)),
        tap({ error: () => this.byProject.delete(projectId) }),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    this.byProject.set(projectId, request);

    return request;
  }

  private rereadProjectAnalyses(
    projectId: string,
    staleSnapshot: Observable<Map<string, ProjectAnalysis>>
  ): Observable<Map<string, ProjectAnalysis>> {
    const latest = this.byProject.get(projectId);
    if (latest && latest !== staleSnapshot) {
      return latest;
    }

    this.byProject.delete(projectId);
    return this.getProjectAnalyses(projectId);
  }

  private buildHeaders(): HttpHeaders {
    const headers: Record<string, string> = { skip: 'true' };

    if (this.suid) {
      headers['Authorization'] = `suid ${this.suid}`;
    }

    return new HttpHeaders(headers);
  }

  private subscribeToQueryParams(): void {
    this.activatedRoute?.queryParams.subscribe((params: Params) => {
      this.suid = params['suid'] || '';
    });
  }
}

function indexByAssetKey(analyses: ProjectAnalysis[] | undefined): Map<string, ProjectAnalysis> {
  const byAssetKey = new Map<string, ProjectAnalysis>();

  for (const analysis of analyses ?? []) {
    if (analysis?.assetKey) {
      byAssetKey.set(analysis.assetKey, analysis);
    }
  }

  return byAssetKey;
}
